import { Link } from 'react-router-dom';
import './branding.css';

/**
 * Single source of truth for the user-facing brand name + visual mark.
 *
 * Imported by `AppHeader`, `AppFooter`, AdminShell, Landing, etc. so a
 * future rename is one constant change instead of a project-wide
 * find/replace. The mark itself is a small inline SVG: a rounded
 * square with a "B." in the brand accent color. No external assets.
 */
export const BRAND_NAME = 'BytaApps';
export const BRAND_TAGLINE = 'Make a model. Ship it anywhere.';

interface BrandLockupProps {
  /** Render as a clickable link to `/` (default true). Pass false on
   *  the landing page itself so it doesn't link to itself. */
  asLink?: boolean;
  /** Optional sub-product label, e.g. "Studio" or "Admin". */
  subBrand?: string;
  /** Visual size: "md" (default) for top bars, "sm" for footers. */
  size?: 'sm' | 'md';
}

/**
 * Brand mark + wordmark. The mark is a 32x32 SVG that doubles as the
 * favicon (see index.html), so visually the in-app logo and the tab
 * icon are the same identity.
 */
export function BrandLockup({
  asLink = true,
  subBrand,
  size = 'md',
}: BrandLockupProps): JSX.Element {
  const inner = (
    <span className={`bl-brand bl-brand--${size}`}>
      <BrandMark />
      <span className="bl-brand__name">
        {BRAND_NAME}
        {subBrand && <span className="bl-brand__sub"> · {subBrand}</span>}
      </span>
    </span>
  );
  if (asLink) {
    return (
      <Link to="/" className="bl-brand__link" aria-label={`${BRAND_NAME} — home`}>
        {inner}
      </Link>
    );
  }
  return inner;
}

/** Just the icon, no wordmark — handy when space is tight. */
export function BrandMark(): JSX.Element {
  return (
    <svg className="bl-brand__mark" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <rect width="32" height="32" rx="7" className="bl-brand__mark-bg" />
      <text x="16" y="22" textAnchor="middle" className="bl-brand__mark-letter">
        B
      </text>
      <circle cx="24" cy="24" r="2.4" className="bl-brand__mark-dot" />
    </svg>
  );
}
